import {
  AfterContentChecked,
  Component,
  Directive,
  Input,
  TemplateRef,
  inject,
  input,
  output,
  contentChildren,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
let nextId = 0;

/**
 * This directive should be used to wrap accordion panel titles that need to contain HTML markup or other directives.
 */
@Directive({ selector: 'ng-template[appPanelTitle]' })
export class PanelTitleDirective {
  templateRef = inject<TemplateRef<any>>(TemplateRef);
}

/**
 * This directive must be used to wrap accordion panel content.
 */
@Directive({ selector: 'ng-template[appPanelContent]' })
export class PanelContentDirective {
  templateRef = inject<TemplateRef<any>>(TemplateRef);
}

/**
 * The Panel directive represents an individual panel with the title and collapsible
 * content
 */
@Directive({ selector: 'app-panel, [appPanel]' })
export class PanelDirective implements AfterContentChecked {
  /**
   *  A flag determining whether the panel is disabled or not.
   *  When disabled, the panel cannot be toggled.
   */
  readonly disabled = input(false);

  /**
   *  An optional id for the panel. The id should be unique.
   *  If not provided, it will be auto-generated.
   */
  readonly id = input(`app-panel-${nextId++}`);

  /**
   * A flag telling if the panel is currently open
   */
  isOpen = false;

  /**
   *  The title for the panel.
   */
  readonly title = input<string>(undefined);

  /**
   *  Accordion's types of panels to be applied per panel basis.
   *  Bootstrap recognizes the following types: "primary", "secondary", "success", "danger", "warning", "info", "light"
   * and "dark"
   */
  @Input() type: string;

  titleTpl: PanelTitleDirective | null;
  contentTpl: PanelContentDirective | null;

  readonly titleTpls = contentChildren(PanelTitleDirective);
  readonly contentTpls = contentChildren(PanelContentDirective);

  ngAfterContentChecked() {
    this.titleTpl = this.titleTpls().at(0)!;
    this.contentTpl = this.contentTpls().at(0)!;
  }
}

/**
 * The payload of the change event fired right before toggling an accordion panel
 */
export interface PanelChangeEvent {
  /**
   * Id of the accordion panel that is toggled
   */
  panelId: string;

  /**
   * Whether the panel will be opened (true) or closed (false)
   */
  nextState: boolean;

  /**
   * Function that will prevent panel toggling if called
   */
  preventDefault: () => void;
}

/**
 * The Accordion directive is a collection of panels.
 * It can assure that only one panel can be opened at a time.
 */
@Component({
    selector: 'app-accordion',
    exportAs: 'Accordion',
    styleUrls: ['./accordion.component.scss'],
    template: `
    @for (panel of panels(); track panel) {
      <div [class]="'card ' + (cardCssClass() || 'bg-lightgray')">
        <div role="tab" id="{{panel.id()}}-header" [class]="'card-header bg-darkgray fw-medium d-flex align-items-center' + (panel.type ? 'bg-'+panel.type: type ? 'bg-'+type : '')">
          <a class="d-flex w-100 text-white"
            (click)="toggle(panel.id())"  [class.collapsed]="!panel.isOpen"
            [attr.aria-expanded]="panel.isOpen" [attr.aria-controls]="panel.id()">
            {{panel.title()}}<ng-template [ngTemplateOutlet]="panel.titleTpl?.templateRef"></ng-template>
          </a>
        </div>
        @if (!destroyOnHide() || panel.isOpen) {
          <div id="{{panel.id()}}" role="tabpanel" [attr.aria-labelledby]="panel.id() + '-header'"
            class="collapse" [class.show]="panel.isOpen">
            <div class="card-body">
              <ng-template [ngTemplateOutlet]="panel.contentTpl?.templateRef"></ng-template>
            </div>
          </div>
        }
      </div>
    }
    `,
    imports: [
      NgTemplateOutlet
    ]
})
export class AccordionComponent implements AfterContentChecked {
  readonly panels = contentChildren(PanelDirective);

  /**
   * An array or comma separated strings of panel identifiers that should be opened
   */
  @Input() activeIds: string | string[] = [];

  /**
   *  Whether the other panels should be closed when a panel is opened
   */
  closeOtherPanels: boolean;

  /**
   * Whether the closed panels should be hidden without destroying them
   */
  readonly destroyOnHide = input(true);

  /**
   *  Accordion's types of panels to be applied globally.
   *  Bootstrap recognizes the following types: "primary", "secondary", "success", "danger", "warning", "info", "light"
   * and "dark
   */
  @Input() type: string;

  /**
   *  Accordion's types of panels to be applied globally.
   *  Bootstrap recognizes the following types: "primary", "secondary", "success", "danger", "warning", "info", "light"
   * and "dark
   */
  readonly cardCssClass = input<string>(undefined);

  /**
   *  Accordion's types of panels to be applied globally.
   *  Bootstrap recognizes the following types: "primary", "secondary", "success", "danger", "warning", "info", "light"
   * and "dark
   */
  readonly cardHeaderCssClass = input<string>(undefined);

  /**
   * A panel change event fired right before the panel toggle happens. See PanelChangeEvent for payload details
   */
  readonly panelChange = output<PanelChangeEvent>();

  /**
   * Checks if a panel with a given id is expanded or not.
   */
  isExpanded(panelId: string): boolean { return this.activeIds.indexOf(panelId) > -1; }

  /**
   * Expands a panel with a given id. Has no effect if the panel is already expanded or disabled.
   */
  expand(panelId: string): void { this._changeOpenState(this._findPanelById(panelId), true); }

  /**
   * Expands all panels if [closeOthers]="false". For the [closeOthers]="true" case will have no effect if there is an
   * open panel, otherwise the first panel will be expanded.
   */
  expandAll(): void {
    if (this.closeOtherPanels) {
      const panels = this.panels();
      if (this.activeIds.length === 0 && panels.length) {
        this._changeOpenState(panels.at(0)!, true);
      }
    } else {
      this.panels().forEach(panel => this._changeOpenState(panel, true));
    }
  }

  /**
   * Collapses a panel with a given id. Has no effect if the panel is already collapsed or disabled.
   */
  collapse(panelId: string) { this._changeOpenState(this._findPanelById(panelId), false); }

  /**
   * Collapses all open panels.
   */
  collapseAll() {
    this.panels().forEach((panel) => { this._changeOpenState(panel, false); });
  }

  /**
   * Programmatically toggle a panel with a given id. Has no effect if the panel is disabled.
   */
  toggle(panelId: string) {
    const panel = this._findPanelById(panelId);
    if (panel) {
      this._changeOpenState(panel, !panel.isOpen);
    }
  }

  isString(value: any): value is string {
    return typeof value === 'string';
  }

  ngAfterContentChecked() {
    // active id updates
    if (this.isString(this.activeIds)) {
      this.activeIds = this.activeIds.split(/\s*,\s*/);
    }

    // update panels open states
    this.panels().forEach(panel => panel.isOpen = !panel.disabled() && this.activeIds.indexOf(panel.id()) > -1);

    // closeOthers updates
    if (this.activeIds.length > 1 && this.closeOtherPanels) {
      this._closeOthers(this.activeIds[0]);
      this._updateActiveIds();
    }
  }

  private _changeOpenState(panel: PanelDirective, nextState: boolean) {
    if (panel && !panel.disabled() && panel.isOpen !== nextState) {
      let defaultPrevented = false;

      const id = panel.id();
      this.panelChange.emit(
          {panelId: id, nextState: nextState, preventDefault: () => { defaultPrevented = true; }});

      if (!defaultPrevented) {
        panel.isOpen = nextState;

        if (nextState && this.closeOtherPanels) {
          this._closeOthers(id);
        }
        this._updateActiveIds();
      }
    }
  }

  private _closeOthers(panelId: string) {
    this.panels().forEach(panel => {
      if (panel.id() !== panelId) {
        panel.isOpen = false;
      }
    });
  }

  private _findPanelById(panelId: string): PanelDirective | null { return this.panels().find(p => p.id() === panelId); }

  private _updateActiveIds() {
    this.activeIds = this.panels().filter(panel => panel.isOpen && !panel.disabled()).map(panel => panel.id());
  }
}
